// The host page's settings (`window.AevoEchoConfig`), the Echo server URL,
// and the business's widget config fetched from Echo.
//
// Server URL, in order of precedence:
//   1. window.AevoEchoConfig.serverUrl (set by the host page before the
//      script loads).
//   2. The origin of the <script> tag that loaded us (document.currentScript
//      at IIFE evaluation time).
//   3. window.location.origin as a last-ditch fallback (only hit when the
//      bundle is loaded in a context that hides currentScript, e.g.
//      dynamically via eval).
//
// Widget config, in order of precedence (later wins):
//   1. The published config Echo serves for `widgetId`, or today's widget
//      when there is none (`legacyWidgetConfig`).
//   2. `AevoEchoConfig.config` / `mount({ config })`: a hand-written subset.
//   3. The host page's older one-off settings (`position`, `draggable`,
//      `resizeCorner`, `conversationTtlMinutes`) when set: they are code on
//      the customer's own page, so they still win.

import { readHostConfig } from './legacy'
import {
  defaultWidgetConfig,
  legacyWidgetConfig,
  normalizeWidgetConfig,
  WIDGET_POSITIONS,
  WIDGET_RESIZE_CORNERS,
  type WidgetConfig,
  type WidgetPosition,
  type WidgetResizeCorner,
} from './widget-config'

export type ResizeCorner = WidgetResizeCorner
/**
 * One of eight perimeter anchors for the floating launcher (no "center
 * center" — covering the middle of the viewport with a chat bubble would
 * fight the host site's content too aggressively).
 */
export type LauncherPosition = WidgetPosition

export interface AevoEchoConfig {
  /** Override the Socket.IO server URL (defaults to the script tag origin). */
  serverUrl?: string
  /** The business's widget (`wgt_…`), from the Aevo widget studio's install tab. */
  widgetId?: string
  /** Set to false to skip auto-mount on DOMContentLoaded. Defaults to true. */
  autoMount?: boolean
  /**
   * Studio preview: never connects, takes its config and state from the
   * parent window over postMessage (see `preview-protocol.ts`).
   */
  preview?: boolean
  /** Origins allowed to drive a preview. Defaults to `document.referrer`'s origin. */
  previewOrigins?: string[]
  /** A WidgetConfig subset applied over the served one (handy for demos and tests). */
  config?: unknown
  /** Where to load the panel bundle from. Defaults to `widget-panel.js` beside `widget.js`. */
  panelUrl?: string
  /**
   * Local inactivity window (minutes) before the saved customer conversation
   * id is discarded on load (localStorage). Defaults to the widget config's
   * memory duration (24h without one). Each page visit bumps activity on
   * connect and when messages arrive. The server separately refuses reuse
   * when the row is `closed`, and for human-owned chats (assigned / escalated
   * / non-open) may mint a new id if the row has been idle for 24h by
   * `updatedAt`. Set to 0 to always start fresh on load.
   */
  conversationTtlMinutes?: number
  /**
   * Which corner of the chat panel users can grab to resize. Defaults to the
   * widget config's (`'top-left'` without one). `'none'` disables resizing.
   */
  resizeCorner?: ResizeCorner
  /** Where the floating launcher sits. Defaults to the widget config's. */
  position?: LauncherPosition
  /** Let the visitor drag the launcher. Defaults to the widget config's. */
  draggable?: boolean
  /**
   * Overrides for embeds without a widget config. Echo's
   * `/widget/v1/config` wins when it has values.
   */
  announcement?: string
  suggestedPrompts?: string[]
}

declare global {
  interface Window {
    AevoEchoConfig?: AevoEchoConfig
  }
}

// Captured at module evaluation (= IIFE top-level). If we wait until a
// later callback, `document.currentScript` will be null. Vite's IIFE
// wrapper runs the entry eagerly, so this fires before DOMContentLoaded.
const bootScript =
  typeof document !== 'undefined'
    ? (document.currentScript as HTMLScriptElement | null)
    : null

function originFromUrl(url: string | undefined): string | null {
  if (!url) return null
  try {
    return new URL(url).origin
  } catch {
    return null
  }
}

export function resolveServerUrl(host: AevoEchoConfig | undefined = readHostConfig()): string {
  const fromConfig = originFromUrl(host?.serverUrl) ?? host?.serverUrl
  if (fromConfig) return fromConfig
  const fromScript = originFromUrl(bootScript?.src)
  if (fromScript) return fromScript
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin
  throw new Error(
    '[aevo-echo] Unable to resolve serverUrl. Set window.AevoEchoConfig.serverUrl before loading the widget.',
  )
}

/** `widget-panel.js` beside the loader script (or under the server's `/widget/v1/`). */
export function resolvePanelUrl(serverUrl: string, version: string, host = readHostConfig()): string {
  if (host?.panelUrl) return host.panelUrl
  let base = `${serverUrl}/widget/v1/`
  if (bootScript?.src) {
    try {
      base = new URL('.', bootScript.src).href
    } catch {
      /* keep the server default */
    }
  }
  return `${base}widget-panel.js?v=${encodeURIComponent(version)}`
}

const DEFAULT_CONVERSATION_TTL_MINUTES = 24 * 60

export function resolveConversationTtlMs(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    return DEFAULT_CONVERSATION_TTL_MINUTES * 60_000
  }
  if (raw === 0) return 0
  if (raw < 0) return -1
  return Math.floor(raw * 60_000)
}

// ---------------------------------------------------------------------------
// GET /widget/v1/config

export interface WidgetRemoteConfig {
  /**
   * `ok`: Echo answered. `unreachable`: no answer (network, 5xx).
   * `origin-not-allowed`: this website isn't one of the widget's allowed ones.
   */
  status: 'ok' | 'unreachable' | 'origin-not-allowed'
  /** The widget Echo knows (null for an unknown or unpublished id). */
  widgetId: string | null
  /** The published WidgetConfig, or null when the embed has none. */
  config: unknown | null
  announcement: string | null
  suggestedPrompts: string[]
  /** True while nobody is available (outside business hours); absent = available. */
  away: boolean
}

const EMPTY_REMOTE: WidgetRemoteConfig = {
  status: 'ok',
  widgetId: null,
  config: null,
  announcement: null,
  suggestedPrompts: [],
  away: false,
}

function cleanPrompts(raw: unknown): string[] {
  return Array.isArray(raw)
    ? raw.filter((p): p is string => typeof p === 'string' && p.trim().length > 0).map((p) => p.trim())
    : []
}

export function parseRemoteConfig(data: unknown): WidgetRemoteConfig {
  if (!data || typeof data !== 'object') return { ...EMPTY_REMOTE }
  const d = data as Record<string, unknown>
  return {
    status: 'ok',
    widgetId: typeof d.widgetId === 'string' && d.widgetId ? d.widgetId : null,
    config: d.config && typeof d.config === 'object' ? d.config : null,
    announcement:
      typeof d.announcement === 'string' && d.announcement.trim() ? d.announcement.trim() : null,
    suggestedPrompts: cleanPrompts(d.suggestedPrompts),
    away: d.away === true,
  }
}

/**
 * Fetch the widget's config from Echo. A 403 `origin-not-allowed` means this
 * page may not show the widget; any other failure answers "no config" and
 * keeps the requested id, so the socket handshake lets Echo decide.
 */
export async function fetchWidgetRemoteConfig(
  serverUrl: string,
  widgetId: string | undefined,
): Promise<WidgetRemoteConfig> {
  const query = widgetId ? `?widget=${encodeURIComponent(widgetId)}` : ''
  const unreachable: WidgetRemoteConfig = { ...EMPTY_REMOTE, status: 'unreachable', widgetId: widgetId ?? null }
  try {
    const res = await fetch(`${serverUrl}/widget/v1/config${query}`, {
      credentials: 'omit',
      cache: 'default',
    })
    if (res.status === 403) {
      const body = (await res.json().catch(() => null)) as { error?: unknown } | null
      if (body?.error === 'origin-not-allowed') return { ...EMPTY_REMOTE, status: 'origin-not-allowed' }
    }
    if (!res.ok) return unreachable
    return parseRemoteConfig(await res.json())
  } catch {
    return unreachable
  }
}

// ---------------------------------------------------------------------------
// Resolution

export interface ResolvedWidget {
  config: WidgetConfig
  /** No widget config anywhere: today's widget (legacy copy and palette). */
  legacy: boolean
  /** The announcement banner text ('' hides it). */
  announcement: string
  conversationTtlMs: number
  away: boolean
}

/**
 * Combine what Echo served with the host page's settings. Pure, so the
 * fallback rules are testable without a network.
 */
export function resolveWidget(
  remote: WidgetRemoteConfig,
  host: AevoEchoConfig | undefined,
  inline: unknown = host?.config,
): ResolvedWidget {
  const hasInline = !!inline && typeof inline === 'object'
  const legacy = !remote.config && !hasInline
  let config = remote.config
    ? normalizeWidgetConfig(remote.config)
    : legacy
      ? legacyWidgetConfig()
      : defaultWidgetConfig()
  if (hasInline) config = normalizeWidgetConfig(inline, config)

  let announcement = config.content.welcome.announcement.text
  if (legacy) {
    const hostAnnouncement =
      typeof host?.announcement === 'string' && host.announcement.trim() ? host.announcement.trim() : null
    announcement = remote.announcement ?? hostAnnouncement ?? ''
    const prompts =
      remote.suggestedPrompts.length > 0 ? remote.suggestedPrompts : cleanPrompts(host?.suggestedPrompts)
    if (prompts.length > 0) config.content.welcome.suggestedPrompts = prompts.slice(0, 8)
  } else if (!announcement && remote.announcement) {
    announcement = remote.announcement
  }

  const placement = config.appearance.placement
  if (host?.position && (WIDGET_POSITIONS as readonly string[]).includes(host.position)) {
    placement.position = host.position
  }
  if (typeof host?.draggable === 'boolean') placement.draggable = host.draggable
  if (host?.resizeCorner && (WIDGET_RESIZE_CORNERS as readonly string[]).includes(host.resizeCorner)) {
    placement.resizeCorner = host.resizeCorner
  }

  const conversationTtlMs = resolveConversationTtlMs(
    typeof host?.conversationTtlMinutes === 'number'
      ? host.conversationTtlMinutes
      : config.content.conversation.memoryMinutes,
  )

  return { config, legacy, announcement, conversationTtlMs, away: remote.away }
}
